/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import adaptivePlugin from '@fullcalendar/adaptive';
import './CalendarStyles.scss';
import { CalendarNavBar } from './CalendarNavBar';
import { CalendarEventContent } from './components/CalendarEventContent';
import { MonthGrid } from './components/MonthGrid';
/** Per-day cap while printing — effectively “all events” for real-world calendars (IP-13259). */
const PRINT_MONTH_VIEW_DAY_MAX_EVENTS = 10000;
/**
 * Promotes all non-standard root fields on a MonthGridEvent into extendedProps,
 * mirroring what FullCalendar does automatically for IFullCalendarEvent objects.
 *
 * CalendarComp (and other consumers) read feature-specific fields like `type`,
 * `data`, and `key` from `fcProps.event.extendedProps.*`. When MonthGrid
 * is the active renderer we must replicate that promotion ourselves so every
 * downstream handler works identically regardless of which renderer is active.
 */
const FC_STANDARD_KEYS = new Set([
    'id',
    'start',
    'end',
    'backgroundColor',
    'borderColor',
    'editable',
    'allDay',
    'display',
    'extendedProps'
]);
function toFcEventShape(event) {
    var _a;
    const inferredExtendedProps = Object.assign({}, ((_a = event.extendedProps) !== null && _a !== void 0 ? _a : {}));
    Object.entries(event).forEach(([k, v]) => {
        if (!FC_STANDARD_KEYS.has(k))
            inferredExtendedProps[k] = v;
    });
    return {
        id: event.id,
        start: event.start,
        end: event.end,
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        editable: event.editable,
        allDay: event.allDay,
        extendedProps: inferredExtendedProps
    };
}
/**
 * Stable factory that creates an eventContent wrapper component outside of React render.
 * Keeps component identity stable across renders and avoids the no-unstable-nested-components
 * lint rule. Called only when dayMaxEvents toggles or eventContent reference changes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeOptimizedEventContent(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
renderEvent
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function OptimizedEventContent(fcProps) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return React.createElement(CalendarEventContent, { fcProps: fcProps, renderEvent: renderEvent });
    }
    return OptimizedEventContent;
}
/**
 * Calendar QUI molecule — wraps FullCalendar with an opinionated API.
 *
 * Consumers never import `@fullcalendar/*` directly. All FullCalendar
 * plugins and configuration are owned by this molecule.
 *
 * When `dayMaxEvents` is active (optimization mode) and the view is
 * `dayGridMonth`, the molecule renders the custom `MonthGrid` instead of
 * FullCalendar. Week and day views always use FullCalendar.
 *
 * Business-logic event components (Visit, TimeOff, Holiday, GenericEvent)
 * stay in the feature layer and are passed via the `eventContent` render prop.
 *
 * Pass an optional `customFilterSection` node to render any filter/action bars
 * between the navigation toolbar and the calendar grid.
 */
export const Calendar = React.memo(React.forwardRef(function Calendar({ events, view, dayMaxEvents, moreLinkClick, eventContent, dayCellContent, eventClassNames, onEventClick, onEventChange, onDatesSet, showNonCurrentDates = false, timeZone, eventOrder, calendarRef, className, customFilterSection, isPrinting = false, schedulerLicenseKey }, ref) {
    const fullCalendarRef = useRef(null);
    // ── Browser-level print detection (handles Ctrl+P in addition to isPrinting prop) ──────
    const [browserIsPrinting, setBrowserIsPrinting] = useState(false);
    useEffect(() => {
        const handleBefore = () => setBrowserIsPrinting(true);
        const handleAfter = () => setBrowserIsPrinting(false);
        window.addEventListener('beforeprint', handleBefore);
        window.addEventListener('afterprint', handleAfter);
        return () => {
            window.removeEventListener('beforeprint', handleBefore);
            window.removeEventListener('afterprint', handleAfter);
        };
    }, []);
    const effectiveIsPrinting = isPrinting || browserIsPrinting;
    // ── Shared view state ─────────────────────────────────────────────────────
    // FullCalendar fires datesSet on mount and every navigation/view change.
    // MonthGrid manages its own month/year separately below.
    const [calendarTitle, setCalendarTitle] = useState('');
    const [currentView, setCurrentView] = useState(view !== null && view !== void 0 ? view : 'dayGridMonth');
    // ── MonthGrid date state ──────────────────────────────────────────────────
    // Only used when the custom MonthGrid is active (dayGridMonth + optimization).
    const today = dayjs();
    const [mgYear, setMgYear] = useState(today.year());
    const [mgMonth, setMgMonth] = useState(today.month());
    // Whether to render MonthGrid instead of FullCalendar.
    // Always use MonthGrid when printing (month view) — FullCalendar uses JS-calculated
    // inline pixel widths for event positioning that don't recalculate for print column
    // widths, causing events to overflow their columns. MonthGrid uses native CSS grid.
    const useMonthGrid = currentView === 'dayGridMonth' && (!!dayMaxEvents || effectiveIsPrinting);
    useImperativeHandle(calendarRef !== null && calendarRef !== void 0 ? calendarRef : ref, () => ({
        requestResize: () => {
            var _a;
            if (!useMonthGrid)
                (_a = fullCalendarRef.current) === null || _a === void 0 ? void 0 : _a.requestResize();
        }
    }), [useMonthGrid]);
    // ── FullCalendar datesSet ─────────────────────────────────────────────────
    const handleDatesSet = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (info) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setCalendarTitle(info.view.title);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setCurrentView(info.view.type);
        // Keep mgYear/mgMonth in sync so MonthGrid shows the correct month when
        // effectiveIsPrinting switches useMonthGrid to true during print.
        // Also mark the bootstrap flag so the mount effect doesn't re-fire
        // fireMonthGridDatesSet — FullCalendar has already satisfied onDatesSet.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const start = info.view.currentStart;
        if (start) {
            setMgYear(start.getFullYear());
            setMgMonth(start.getMonth());
            initialDatesSetFiredRef.current = true;
        }
        onDatesSet === null || onDatesSet === void 0 ? void 0 : onDatesSet(info);
    }, [onDatesSet]);
    // ── Navigation ────────────────────────────────────────────────────────────
    // MonthGrid and FullCalendar share the same CalendarNavBar, so navigation
    // handlers dispatch to the right implementation depending on the active renderer.
    const fireMonthGridDatesSet = useCallback((year, month) => {
        // Fall back to the browser's IANA timezone when no timeZone prop is provided,
        // matching FullCalendar's behaviour when timeZone is undefined.
        const tz = timeZone !== null && timeZone !== void 0 ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
        const monthStr = String(month + 1).padStart(2, '0');
        // Midnight on the 1st in the configured timezone, built with luxon: dayjs-tz keeps the
        // starting offset through .add(1, 'month'), which cut November's range an hour short.
        const startTz = DateTime.fromISO(`${year}-${monthStr}-01`, { zone: tz });
        // FullCalendar's end is exclusive — first day of the following month.
        const endTz = startTz.plus({ months: 1 });
        const title = dayjs(new Date(year, month)).format('MMMM YYYY');
        setCalendarTitle(title);
        onDatesSet === null || onDatesSet === void 0 ? void 0 : onDatesSet({
            start: startTz.toJSDate(),
            end: endTz.toJSDate(),
            // Timezone-aware ISO strings (e.g. "2026-03-01T00:00:00-05:00") so the
            // API receives the correct UTC boundary for the configured timezone.
            startStr: startTz.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"),
            endStr: endTz.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"),
            view: { type: 'dayGridMonth', title }
        });
    }, [onDatesSet, timeZone]);
    const handlePrev = useCallback(() => {
        var _a;
        if (useMonthGrid) {
            const prev = dayjs(new Date(mgYear, mgMonth, 1)).subtract(1, 'month');
            setMgYear(prev.year());
            setMgMonth(prev.month());
            fireMonthGridDatesSet(prev.year(), prev.month());
        }
        else {
            (_a = fullCalendarRef.current) === null || _a === void 0 ? void 0 : _a.getApi().prev();
        }
    }, [useMonthGrid, mgYear, mgMonth, fireMonthGridDatesSet]);
    const handleNext = useCallback(() => {
        var _a;
        if (useMonthGrid) {
            const next = dayjs(new Date(mgYear, mgMonth, 1)).add(1, 'month');
            setMgYear(next.year());
            setMgMonth(next.month());
            fireMonthGridDatesSet(next.year(), next.month());
        }
        else {
            (_a = fullCalendarRef.current) === null || _a === void 0 ? void 0 : _a.getApi().next();
        }
    }, [useMonthGrid, mgYear, mgMonth, fireMonthGridDatesSet]);
    const handleToday = useCallback(() => {
        var _a;
        if (useMonthGrid) {
            setMgYear(today.year());
            setMgMonth(today.month());
            fireMonthGridDatesSet(today.year(), today.month());
        }
        else {
            (_a = fullCalendarRef.current) === null || _a === void 0 ? void 0 : _a.getApi().today();
        }
    }, [useMonthGrid, today, fireMonthGridDatesSet]);
    const handleViewChange = useCallback((newView) => {
        var _a;
        // Capture whether MonthGrid is active BEFORE the state update, so we
        // know if FullCalendar is currently mounted (and its ref is valid).
        const wasUsingMonthGrid = currentView === 'dayGridMonth' && !!dayMaxEvents;
        setCurrentView(newView);
        if (newView === 'dayGridMonth' && dayMaxEvents) {
            // Switching (back) to MonthGrid — FullCalendar won't fire datesSet,
            // so fire it manually so the feature layer refetches.
            fireMonthGridDatesSet(mgYear, mgMonth);
        }
        else if (!wasUsingMonthGrid) {
            // Already in FullCalendar mode (week ↔ day, or month without
            // optimization) — ref is valid, just call changeView.
            (_a = fullCalendarRef.current) === null || _a === void 0 ? void 0 : _a.getApi().changeView(newView);
        }
        // wasUsingMonthGrid && newView !== 'dayGridMonth':
        // FullCalendar ref is null now but FullCalendar will mount fresh.
        // It picks up the correct view via initialView={currentView} below.
    }, [currentView, dayMaxEvents, mgYear, mgMonth, fireMonthGridDatesSet]);
    // ── Update title for MonthGrid ─────────────────────────────────────────────
    // On mount and when month/year changes, sync the CalendarNavBar title
    // without needing a FullCalendar datesSet event.
    useMemo(() => {
        if (useMonthGrid) {
            setCalendarTitle(dayjs(new Date(mgYear, mgMonth, 1)).format('MMMM YYYY'));
        }
    }, [useMonthGrid, mgYear, mgMonth]);
    // ── Initial onDatesSet for MonthGrid ──────────────────────────────────────
    // FullCalendar fires datesSet automatically on mount. When MonthGrid is the
    // initial renderer, FullCalendar never mounts so we must fire it ourselves.
    // This is the signal the feature layer uses to trigger its first API fetch.
    const initialDatesSetFiredRef = useRef(false);
    useEffect(() => {
        if (useMonthGrid && !initialDatesSetFiredRef.current) {
            initialDatesSetFiredRef.current = true;
            fireMonthGridDatesSet(mgYear, mgMonth);
        }
    }, [useMonthGrid]); // run once when MonthGrid mode first activates
    // ── Timezone change for MonthGrid ─────────────────────────────────────────
    // FullCalendar handles timeZone prop changes reactively and fires datesSet
    // automatically. MonthGrid does not — nothing called fireMonthGridDatesSet
    // when timeZone changed, so events stayed frozen in the old timezone.
    // Track the previous value to skip the initial mount (already handled above).
    const prevTimeZoneRef = useRef(timeZone);
    useEffect(() => {
        const prev = prevTimeZoneRef.current;
        prevTimeZoneRef.current = timeZone;
        if (useMonthGrid && prev !== timeZone) {
            fireMonthGridDatesSet(mgYear, mgMonth);
        }
    }, [timeZone, useMonthGrid, fireMonthGridDatesSet, mgYear, mgMonth]);
    // ── FullCalendar eventContent ─────────────────────────────────────────────
    // When dayMaxEvents is active, wrap the caller's eventContent with
    // CalendarEventContent so hidden overflow events render as <span /> instead
    // of their full component tree. This eliminates ~9,300 wasted DOM nodes per
    // month view (930 hidden events × ~10 nodes). Pass through unchanged otherwise.
    const wrappedEventContent = useMemo(() => {
        if (!dayMaxEvents || effectiveIsPrinting || !eventContent)
            return eventContent;
        return makeOptimizedEventContent(eventContent);
    }, [dayMaxEvents, effectiveIsPrinting, eventContent]);
    // ── MonthGrid adapter callbacks ───────────────────────────────────────────
    // MonthGrid receives typed MonthGridEvent objects. The feature layer's callbacks
    // expect FullCalendar-shaped objects. These adapters bridge the gap.
    const handleMonthGridEventClick = useCallback((event) => {
        onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick({ event: toFcEventShape(event) });
    }, [onEventClick]);
    const handleMonthGridEventChange = useCallback((event, newStart, newEnd) => {
        onEventChange === null || onEventChange === void 0 ? void 0 : onEventChange({
            event: Object.assign(Object.assign({}, toFcEventShape(event)), { start: newStart, end: newEnd })
        });
    }, [onEventChange]);
    const monthGridEventContent = useCallback((event) => {
        if (!eventContent)
            return null;
        return eventContent({ event: toFcEventShape(event) });
    }, [eventContent]);
    // ── Print time range for day view ─────────────────────────────────────
    // When printing in day view, limit the visible time range to where events
    // exist (with 1-hour padding). This eliminates blank hours and gives each
    // event more vertical space so they don't overlap in the print output.
    const printTimeRange = useMemo(() => {
        if (!effectiveIsPrinting || currentView !== 'timeGridDay')
            return {};
        const timedEvents = events.filter((e) => !e.allDay && e.start);
        if (timedEvents.length === 0)
            return {};
        // Extract the hour-of-day in the calendar's configured timezone so the
        // computed range matches what FullCalendar renders. Falls back to the
        // browser locale when no explicit timezone is configured.
        const calTz = timeZone !== null && timeZone !== void 0 ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
        // Hoist formatter to avoid re-creating it per event. Use hourCycle:'h23'
        // instead of hour12:false — 'en-US' with hour12:false resolves to h24,
        // which returns 24 at midnight instead of 0 and corrupts the range.
        const hourFormatter = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            hourCycle: 'h23',
            timeZone: calTz
        });
        const getHourInTz = (date) => {
            var _a, _b;
            const parts = hourFormatter.formatToParts(date);
            const hour = Number.parseInt((_b = (_a = parts.find((p) => p.type === 'hour')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '0', 10);
            return hour % 24;
        };
        const { minHour, maxHour } = timedEvents.reduce((acc, event) => {
            const startDate = new Date(event.start);
            // Avoid nested ternary: resolve the end date in two steps.
            const rawEnd = event.end ? new Date(event.end) : null;
            const endDate = rawEnd !== null && rawEnd !== void 0 ? rawEnd : new Date(startDate.getTime() + 60 * 60 * 1000);
            const startH = getHourInTz(startDate);
            const endH = getHourInTz(endDate);
            // If the event spans midnight (end hour is strictly before start hour
            // while end is chronologically after start), clamp to 24 so the full
            // remaining portion of the day is visible.
            const isOvernight = endDate.getTime() > startDate.getTime() && endH < startH;
            const effectiveEndH = isOvernight ? 24 : endH;
            return {
                minHour: Math.min(acc.minHour, startH),
                maxHour: Math.max(acc.maxHour, effectiveEndH)
            };
        }, { minHour: 24, maxHour: 0 });
        // Add 1-hour padding on each side
        const paddedMin = Math.max(0, minHour - 1);
        const paddedMax = Math.min(24, maxHour + 1);
        return {
            slotMinTime: `${String(paddedMin).padStart(2, '0')}:00:00`,
            slotMaxTime: `${String(paddedMax).padStart(2, '0')}:00:00`
        };
    }, [effectiveIsPrinting, currentView, events, timeZone]);
    // ── Print day slot height ──────────────────────────────────────────────
    // Calculate the slot height so the time grid fills a US Letter portrait page
    // (~880px for the time grid area). Applied via a CSS variable on the wrapper
    // div (NOT inside @media print) so FullCalendar sees the taller slots during
    // its layout calculation and positions events correctly.
    const printDaySlotHeight = useMemo(() => {
        if (!printTimeRange.slotMinTime || !printTimeRange.slotMaxTime)
            return null;
        const minH = parseInt(printTimeRange.slotMinTime, 10);
        const maxH = parseInt(printTimeRange.slotMaxTime, 10);
        const totalSlots = (maxH - minH) * 2; // 30-min default slots
        // 880px ≈ US Letter portrait printable height minus header/all-day.
        // Never go below 42px (FullCalendar's default screen slot height).
        return Math.max(42, Math.floor(880 / totalSlots));
    }, [printTimeRange.slotMinTime, printTimeRange.slotMaxTime]);
    const monthGridEvents = useMemo(() => events, [events]);
    const monthGridDayMaxEvents = useMemo(() => {
        if (effectiveIsPrinting)
            return PRINT_MONTH_VIEW_DAY_MAX_EVENTS;
        if (typeof dayMaxEvents === 'number')
            return dayMaxEvents;
        return 4;
    }, [effectiveIsPrinting, dayMaxEvents]);
    const wrapperClassName = ['qmb-ui-calendar', className, printDaySlotHeight ? 'qmb-ui-calendar--print-day' : '']
        .filter(Boolean)
        .join(' ');
    const wrapperStyle = printDaySlotHeight
        ? // eslint-disable-next-line @typescript-eslint/naming-convention
            { '--fc-print-slot-height': `${printDaySlotHeight}px` }
        : undefined;
    return (React.createElement("div", { className: wrapperClassName, style: wrapperStyle },
        React.createElement(CalendarNavBar, { title: calendarTitle, view: currentView, onPrev: handlePrev, onNext: handleNext, onToday: handleToday, onViewChange: handleViewChange }),
        customFilterSection != null && React.createElement("div", { className: "qmb-ui-calendar__filter-section" }, customFilterSection),
        useMonthGrid ? (React.createElement(MonthGrid, { year: mgYear, month: mgMonth, timezone: timeZone !== null && timeZone !== void 0 ? timeZone : Intl.DateTimeFormat().resolvedOptions().timeZone, events: monthGridEvents, dayMaxEvents: monthGridDayMaxEvents, eventContent: monthGridEventContent, onEventClick: handleMonthGridEventClick, onEventChange: handleMonthGridEventChange })) : (React.createElement(FullCalendar, Object.assign({ plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin, adaptivePlugin], ref: fullCalendarRef, schedulerLicenseKey: schedulerLicenseKey, headerToolbar: false, height: effectiveIsPrinting ? 'auto' : '100%', expandRows: true, eventContent: wrappedEventContent, dayCellContent: dayCellContent, initialView: currentView, 
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            events: events, eventClassNames: eventClassNames, datesSet: handleDatesSet, eventDisplay: "block", eventStartEditable: true, selectable: false, eventResizableFromStart: false, eventClick: onEventClick, eventChange: onEventChange, dayMaxEvents: effectiveIsPrinting ? false : dayMaxEvents, moreLinkClick: effectiveIsPrinting ? undefined : moreLinkClick, dragScroll: true, showNonCurrentDates: showNonCurrentDates, timeZone: timeZone, eventOrder: eventOrder !== null && eventOrder !== void 0 ? eventOrder : 'start,-duration,allDay,title' }, printTimeRange)))));
}));
Calendar.displayName = 'Calendar';
